// epmaClient — AUTHENTICATED staff client for the standalone Exclusive Property
// Management Agreement (EPMA).
//
// One authed action: initiate the agreement. Staff have entered EVERY value
// (owners, property, term, compensation, §21 checkboxes, rents, utilities,
// winterization, other items — zero AppFolio data); this POSTs the lot and the
// backend builds the DocuSeal roster (owners sign, then Miles as Agent, then
// Christy as Broker) and sends it for signature.
//
// The feature is DORMANT by default behind EPMA_SEND_ENABLED (+ WHP_OWN_MESSAGING)
// — it's a legal document, so until Miles flips the flag the initiate route
// answers 503. We surface that as `EpmaNotEnabledError` so the page can render a
// friendly "not turned on yet" state (mirrors paymentPlanClient's 503 handling).

import { api, ApiError } from './api';

// --- Owner / input wire shapes (1:1 with apps/api/src/lib/epmaDoc.ts) ---

export type EpmaOwnerType = 'individual' | 'entity';

export interface EpmaOwnerInput {
  ownerType: EpmaOwnerType;
  firstName: string;
  lastName: string;
  /** Signing email = §25 notices email = the one we send to. */
  email: string;
  /** Entity legal name — required for an entity owner. */
  entityName?: string;
  /** The signer's title/capacity at the entity. */
  signerTitle?: string;
}

export type EpmaPropertyType =
  | 'Single Family Residence'
  | 'Multi Family Residence'
  | 'Commercial';

export type EpmaUtilityChoice = 'included' | 'not_included';

export interface EpmaInputBody {
  propertyAddress: string;
  propertyType: EpmaPropertyType;
  numberOfUnits?: number;
  /** yyyy-mm-dd effective start date. The term is FIXED at one year (source prose);
   *  endDate = start + 1 year − 1 day, pre-computed server-side. */
  startDate: string;
  managementPercent: number;
  placementPercent: number;
  hoaFees: boolean;
  propertyTaxes: boolean;
  mortgage: boolean;
  insurancePremiums: boolean;
  targetRents: string;
  minimumRents: string;
  utilities: {
    water: EpmaUtilityChoice;
    sewer: EpmaUtilityChoice;
    garbage: EpmaUtilityChoice;
    electricity: EpmaUtilityChoice;
    gas: EpmaUtilityChoice;
    yardCare: EpmaUtilityChoice;
  };
  /** Whether the property has an irrigation system. Gates the ENTIRE
   *  winterization section in the rendered agreement (defaults true server-side). */
  hasIrrigation: boolean;
  irrigationWinterization: boolean;
  hasWinterizationInstructions: boolean;
  winterizationInstructions?: string;
  otherItems?: string;
  /** §25 Notices — the SINGLE JOINT "If to Owner" mailing address (entered once;
   *  notice to joint owners goes to ONE party). The name defaults server-side to
   *  the joined owner party names; noticeName overrides it. */
  noticeName?: string;
  noticeAddress?: string;
  noticeCityStateZip?: string;
  noticeEmail?: string;
}

/** Body for POST /api/property-management-agreement/initiate. */
export interface InitiateEpmaBody {
  owners: EpmaOwnerInput[];
  input: EpmaInputBody;
}

/** Backend response. */
export interface InitiateEpmaResult {
  groupId: string;
  /** How many signing requests went out (the order-0 owners). */
  sent: number;
  /** The roster the agreement went to (owners + Agent + Broker). */
  recipients: Array<{ role: string; name: string; hasEmail: boolean }>;
}

/**
 * Thrown when the initiate route answers 503 — the feature is gated off
 * (EPMA_SEND_ENABLED / WHP_OWN_MESSAGING). The page catches this to render the
 * friendly "not enabled yet" state rather than an error toast.
 */
export class EpmaNotEnabledError extends Error {
  constructor(message = 'Property management agreement sending is not enabled yet.') {
    super(message);
    this.name = 'EpmaNotEnabledError';
  }
}

/** A simple email shape check (matches the backend's permissiveness). */
export function isEmailValid(email: string): boolean {
  const e = email.trim();
  return e.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/** Whether an owner row is complete enough to send. */
export function isOwnerRowValid(o: EpmaOwnerInput): boolean {
  const hasName = o.firstName.trim().length > 0 && o.lastName.trim().length > 0;
  if (!hasName) return false;
  if (!isEmailValid(o.email)) return false;
  if (o.ownerType === 'entity' && !(o.entityName ?? '').trim()) return false;
  return true;
}

/**
 * Initiate the EPMA: render it from the owners + input and send it for signature.
 * Throws EpmaNotEnabledError on a 503 (feature dormant); other errors propagate as
 * ApiError for the caller's catch.
 */
export async function initiateEpma(body: InitiateEpmaBody): Promise<InitiateEpmaResult> {
  try {
    const raw = await api.post<{
      groupId: string;
      sent?: number;
      recipients?: Array<{ role: string; name: string; hasEmail: boolean }>;
    }>('/api/property-management-agreement/initiate', body);
    return {
      groupId: raw.groupId,
      sent: raw.sent ?? 0,
      recipients: raw.recipients ?? [],
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new EpmaNotEnabledError(
        ((err.body as { error?: string } | null)?.error) ??
          'Property management agreement sending is not enabled yet.',
      );
    }
    throw err;
  }
}
